import { api } from './api';

export interface Training {
  id: string;
  title: string;
  description?: string;
  thumbnailUrl?: string;
  thumbnailPresignedUrl?: string | null;
  order: number;
  _count?: { lessons: number };
  lessons?: AdminLessonSummary[];
}

export interface AdminLessonSummary {
  id: string;
  title: string;
  order: number;
  status: string;
  isActive: boolean;
  duration: number;
  createdAt: string;
  quiz?: unknown[] | null;
}

export interface Lesson {
  id: string;
  trainingId: string;
  title: string;
  description?: string;
  videoUrl?: string;
  duration: number;
  order: number;
  transcript?: string;
  transcriptTimeline?: TranscriptEntry[] | null;
  quiz?: QuizQuestion[] | null;
  status?: string;
  isActive?: boolean;
  storagePath?: string | null;
  fileName?: string | null;
  mimeType?: string | null;
  errorMessage?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface TranscriptEntry {
  speaker: string;
  startMs: number;
  endMs: number;
  startLabel: string;
  endLabel: string;
  text: string;
}

export interface QuizQuestion {
  question: string;
  options: string[];
  correctIndex: number;
}

export interface ProgressRecord {
  lessonId: string;
  completed: boolean;
  completedAt?: string;
  quizScore?: number | null;
  quizPassed?: boolean;
  watchedDuration?: number;
}

export interface TrainingProgress {
  total: number;
  completed: number;
  percentage: number;
  records: ProgressRecord[];
}

export interface QuizResult {
  score: number;
  passed: boolean;
  correct: number;
  total: number;
}

export const trainingService = {
  // Student
  findAll: () => api.get<Training[]>('/trainings'),
  findOne: (id: string) => api.get<Omit<Training, 'lessons'> & { lessons: Lesson[] }>(`/trainings/${id}`),
  findLessons: (trainingId: string) => api.get<Lesson[]>(`/trainings/${trainingId}/lessons`),
  resolveLesson: (lessonId: string) =>
    api.get<{ lessonId: string; trainingId: string }>(`/trainings/lessons/${lessonId}/location`),
  getLesson: (trainingId: string, lessonId: string) =>
    api.get<Lesson & { progress: ProgressRecord | null }>(`/trainings/${trainingId}/lessons/${lessonId}`),
  getVideoUrl: (trainingId: string, lessonId: string) =>
    api.get<{ videoUrl: string }>(`/trainings/${trainingId}/lessons/${lessonId}/video-url`),
  getProgress: () => api.get<TrainingProgress>('/trainings/progress/me'),
  markProgress: (lessonId: string, completed: boolean) =>
    api.post<ProgressRecord>('/trainings/progress', { lessonId, completed }),
  updateWatchProgress: (trainingId: string, lessonId: string, watchedDuration: number) =>
    api.post<ProgressRecord>(`/trainings/${trainingId}/lessons/${lessonId}/progress`, { watchedDuration }),
  submitQuiz: (trainingId: string, lessonId: string, answers: number[]) =>
    api.post<QuizResult>(`/trainings/${trainingId}/lessons/${lessonId}/quiz`, { answers }),

  // Admin
  adminList: () => api.get<Training[]>('/admin/trainings'),
  adminGet: (id: string) => api.get<Training & { lessons: Lesson[] }>(`/admin/trainings/${id}`),
  adminCreate: (data: { title: string; description?: string; thumbnailUrl?: string; order?: number }) =>
    api.post<Training>('/admin/trainings', data),
  adminUpdate: (id: string, data: Partial<{ title: string; description?: string; thumbnailUrl?: string; order?: number }>) =>
    api.patch<Training>(`/admin/trainings/${id}`, data),
  adminDelete: (id: string) => api.delete<{ success: boolean }>(`/admin/trainings/${id}`),
  adminCreateLesson: (trainingId: string, data: { title: string; description?: string; videoUrl?: string; order?: number }) =>
    api.post<Lesson>(`/admin/trainings/${trainingId}/lessons`, data),
  adminGetLesson: (lessonId: string) => api.get<Lesson & { videoUrl?: string }>(`/admin/trainings/lessons/${lessonId}`),
  adminUpdateLesson: (lessonId: string, data: Partial<Lesson>) =>
    api.patch<Lesson>(`/admin/trainings/lessons/${lessonId}`, data),
  adminDeleteLesson: (lessonId: string) => api.delete<{ success: boolean }>(`/admin/trainings/lessons/${lessonId}`),
  adminReorderTrainings: (orders: { id: string; order: number }[]) =>
    api.post<{ success: boolean }>('/admin/trainings/reorder', { orders }),
  adminReorderLessons: (orders: { id: string; order: number }[]) =>
    api.post<{ success: boolean }>('/admin/trainings/lessons/reorder', { orders }),
  adminPresignThumbnail: (trainingId: string, fileName: string, mimeType: string) =>
    api.post<{ uploadUrl: string; s3Key: string }>(`/admin/trainings/${trainingId}/thumbnail/presign`, { fileName, mimeType }),
  adminConfirmThumbnail: (trainingId: string, s3Key: string) =>
    api.post<{ thumbnailUrl: string }>(`/admin/trainings/${trainingId}/thumbnail/confirm`, { s3Key }),
  adminUploadVideo: (lessonId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.postForm<{ status: string }>(`/admin/trainings/lessons/${lessonId}/upload`, form);
  },
};
